<template>
  <div
    :aria-label="accessibleLabel"
    :aria-valuemax="safeTotal"
    aria-valuemin="0"
    :aria-valuenow="safeProgress"
    class="tw:h-2.5 tw:bg-denim-300 tw:rounded-full tw:overflow-hidden"
    data-testid="progress-bar"
    role="progressbar"
  >
    <div
      :class="
        twMerge(
          'tw:h-full tw:bg-blue-500 tw:opacity-100 tw:transition-[width] tw:duration-300 tw:ease-out',
          color
        )
      "
      data-testid="progress-bar-progress"
      :style="progressStyle"
    />
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, type CSSProperties, onMounted, onUnmounted, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";

export interface ProgressBarProps {
  ariaLabel?: string;
  color?: string;
  progress: number;
  total: number;
}

const props = defineProps<ProgressBarProps>();

const hasMounted = ref<boolean>(false);

const accessibleLabel = computed<string>(
  () => props.ariaLabel || getLocaleText("progress_bar.label")
);

const safeTotal = computed<number>(() => Math.max(props.total, 0));

const safeProgress = computed<number>(() =>
  Math.max(Math.min(props.progress, safeTotal.value), 0)
);

const progressPercentage = computed<number>(() => {
  if (safeTotal.value <= 0) {
    return 0;
  }

  return Math.ceil((safeProgress.value / safeTotal.value) * 100);
});

const progressStyle = computed<CSSProperties>(() => ({
  width: `${hasMounted.value ? progressPercentage.value : 0}%`,
}));

let mountTimer: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
  mountTimer = setTimeout(() => {
    hasMounted.value = true;
  }, 300);
});

onUnmounted(() => {
  if (mountTimer) {
    clearTimeout(mountTimer);
  }
});
</script>
